import React, { useEffect, useState, Suspense, lazy } from "react";
import PWAPrompt from "./components/PWAPrompt";
import "./index.css";
import { useApp } from "./context/AppContext";
import AuthPage       from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import Sidebar        from "./components/Sidebar";
import { NAV_ITEMS }  from "./data";
import Footer from "./components/Footer";
import supabase from "./lib/supabase";

// ── Lazy loaded pages — only downloaded when first visited ────────────────────
const HomePage      = lazy(() => import("./pages/HomePage"));
const JobsPage      = lazy(() => import("./pages/JobsPage"));
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const NetworkPage   = lazy(() => import("./pages/NetworkPage"));
const MessagesPage  = lazy(() => import("./pages/MessagesPage"));
const AlertsPage    = lazy(() => import("./pages/AlertsPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const SalaryPage    = lazy(() => import("./pages/SalaryPage"));
const InterviewPage = lazy(() => import("./pages/InterviewPage"));
const ResumePage    = lazy(() => import("./pages/ResumePage"));
const SettingsPage  = lazy(() => import("./pages/SettingsPage"));
const AIMatchingPage   = lazy(() => import("./pages/AIMatchingPage"));
const CoverLetterPage  = lazy(() => import("./pages/CoverLetterPage"));
const ProfilePage      = lazy(() => import("./pages/ProfilePage"));
const PrivacyPolicy    = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService   = lazy(() => import("./pages/TermsOfService"));
const NotFoundPage     = lazy(() => import("./pages/NotFoundPage"));
const DeleteDataPage   = lazy(() => import("./pages/DeleteDataPage"));
const AboutPage        = lazy(() => import("./pages/AboutPage"));

// ── Employer portal — largest chunk, lazy loaded separately ──────────────────
const EmployerPage  = lazy(() => import("./pages/EmployerPage"));
const EmployerShell = lazy(() => import("./components/EmployerShell"));

const PAGES = {
  home:        HomePage,
  jobs:        JobsPage,
  companies:   CompaniesPage,
  network:     NetworkPage,
  messages:    MessagesPage,
  alerts:      AlertsPage,
  analytics:   AnalyticsPage,
  salary:      SalaryPage,
  interview:   InterviewPage,
  resume:      ResumePage,
  settings:    SettingsPage,
  aimatching:  AIMatchingPage,
  coverletter: CoverLetterPage,
  profile:     ProfilePage,
  privacy:     PrivacyPolicy,
  terms:       TermsOfService,
  employers:   EmployerPage,
  about:       AboutPage,
};

// ── Page loading fallback ─────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"60vh" }}>
      <div style={{ width:24, height:24, border:"2.5px solid rgba(123,111,232,0.2)", borderTopColor:"#7B6FE8", borderRadius:"50%", animation:"spin 0.9s linear infinite" }} />
    </div>
  );
}

// ── Error Boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Vorsia ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight:"100vh", background:"#0a0a0f", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:20, padding:24, textAlign:"center" }}>
          <img src="/vorsia_ai.png" alt="Vorsia" style={{ width:64, height:64, objectFit:"contain", borderRadius:14, marginBottom:8 }} />
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:"#f0eeff" }}>
            Something went wrong.
          </div>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, color:"rgba(255,255,255,0.5)", maxWidth:360, lineHeight:1.6 }}>
            An unexpected error occurred. Your data is safe — please refresh the page to continue.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }}
            style={{ background:"linear-gradient(135deg,#29ABE2,#9B35C8)", border:"none", color:"#fff", padding:"12px 28px", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
            Return to Vorsia →
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Page-level Error Boundary ─────────────────────────────────────────────────
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Page error:", error, info);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pageKey !== this.props.pageKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"64px 24px", textAlign:"center", gap:14 }}>
          <div style={{ fontSize:36 }}>⚠️</div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:"var(--text-primary)" }}>
            This page ran into a problem.
          </div>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color:"var(--text-secondary)", maxWidth:320, lineHeight:1.6 }}>
            The rest of the app is still working. Try navigating to another page or refreshing.
          </p>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{ background:"var(--accent)", border:"none", color:"#fff", padding:"10px 22px", borderRadius:9, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Loading screen ────────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight:"100vh", background:"#0a0a0f", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:20 }}>
      <img src="/vorsia_ai.png" alt="Vorsia" style={{ width:72, height:72, objectFit:"contain", borderRadius:16 }} />
      <div style={{ width:28, height:28, border:"2.5px solid rgba(123,111,232,0.2)", borderTopColor:"#7B6FE8", borderRadius:"50%", animation:"spin 0.9s linear infinite" }} />
    </div>
  );
}

// ── Main app shell ────────────────────────────────────────────────────────────
function Shell() {
  const { page, navigate, toast, profile, user } = useApp();
  const [employerPage,  setEmployerPage]  = useState("dashboard");
  const [offerCount,    setOfferCount]    = useState(0);
  const [unreadAlerts,  setUnreadAlerts]  = useState(0);

  // ── Load real topbar data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      try {
        const [extOffers, natOffers, alertCount] = await Promise.all([
          supabase
            .from("applications")
            .select("id", { count:"exact" })
            .eq("user_id", user.id)
            .eq("status", "Offer"),
          supabase
            .from("job_applications")
            .select("id", { count:"exact" })
            .eq("applicant_id", user.id)
            .eq("status", "Offer"),
          supabase
            .from("messages")
            .select("id", { count:"exact" })
            .eq("recipient_id", user.id)
            .eq("read", false),
        ]);
        setOfferCount((extOffers.count || 0) + (natOffers.count || 0));
        setUnreadAlerts(alertCount.count || 0);
      } catch(e) {
        console.error("Topbar data error:", e);
      }
    };
    load();
    if (page === "alerts" || page === "messages") setUnreadAlerts(0);
  }, [user?.id, page]);

  useEffect(() => {
    const handler = (e) => navigate(e.detail);
    window.addEventListener("vorsia-navigate", handler);
    return () => window.removeEventListener("vorsia-navigate", handler);
  }, [navigate]);

  if (profile?.role === "employer") {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <EmployerShell page={employerPage} setPage={setEmployerPage} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  const PageComponent = PAGES[page] || (page ? NotFoundPage : HomePage);
  const pageLabel = NAV_ITEMS.find(n => n.id === page)?.label || "Settings";

  return (
    <div style={{ display:"flex", height:"100vh", background:"var(--bg-primary)", color:"var(--text-primary)", fontFamily:"'DM Sans',sans-serif", overflow:"hidden" }}>
      {toast && (
        <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", zIndex:500, background:"var(--bg-secondary)", border:"1px solid rgba(74,222,128,0.3)", borderRadius:12, padding:"12px 24px", fontSize:13, fontWeight:600, color:"#4ade80", boxShadow:"var(--shadow)", animation:"slideDown 0.3s ease", whiteSpace:"nowrap" }}>
          {toast}
        </div>
      )}

      <Sidebar />

      <main style={{ flex:1, overflowY:"auto", padding:"28px 32px" }}>
        {/* Topbar */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24, borderBottom:"1px solid var(--border)", paddingBottom:16 }}>
          <div style={{ fontSize:11, color:"var(--text-tertiary)", letterSpacing:1 }}>
            Vorsia <span style={{ color:"var(--border)" }}>›</span>{" "}
            <span style={{ color:"var(--text-secondary)" }}>{pageLabel}</span>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {offerCount > 0 && (
              <button onClick={() => navigate("analytics")}
                style={{ fontSize:11, color:"rgba(74,222,128,0.7)", background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.15)", padding:"3px 10px", borderRadius:20, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                ● {offerCount} Offer{offerCount > 1 ? "s" : ""} Pending
              </button>
            )}
            <button onClick={() => navigate("alerts")} style={{ position:"relative", background:"var(--bg-hover)", border:"1px solid var(--border)", color:"var(--text-secondary)", width:32, height:32, borderRadius:8, cursor:"pointer", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center" }}>
              🔔
              {unreadAlerts > 0 && (
                <div style={{ position:"absolute", top:-3, right:-3, width:12, height:12, borderRadius:"50%", background:"#7B6FE8", fontSize:8, fontWeight:700, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", border:"1.5px solid var(--bg-primary)" }}>
                  {unreadAlerts > 9 ? "9+" : unreadAlerts}
                </div>
              )}
            </button>
          </div>
        </div>

        <PageErrorBoundary pageKey={page}>
          <Suspense fallback={<PageLoader />}>
            <PageComponent key={page} />
          </Suspense>
        </PageErrorBoundary>
        <Footer />
      </main>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  const { user, profile, authLoading, setProfile } = useApp();
  const needsOnboarding = user && !profile?.onboarded;

  if (typeof window !== "undefined") {
    if (localStorage.getItem("vorsia-theme") === "light") {
      document.body.classList.add("light");
    }
  }

  const hash = window.location.hash.replace("#","");
  const query = new URLSearchParams(window.location.search).get("page");
  const pathname = window.location.pathname;

  const publicPage = hash === "employers" || query === "employers";
  if (publicPage) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <EmployerPage />
      </Suspense>
    );
  }

  // Handle public pages accessible without auth
  // Supports both clean URLs (/privacy) and query param URLs (?page=privacy)
  if (query === "about" || pathname === "/about") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <AboutPage />
      </Suspense>
    );
  }
  if (query === "privacy" || pathname === "/privacy") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PrivacyPolicy />
      </Suspense>
    );
  }
  if (query === "terms" || pathname === "/terms") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TermsOfService />
      </Suspense>
    );
  }
  if (query === "delete-data" || pathname === "/delete-data") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <DeleteDataPage />
      </Suspense>
    );
  }

  const knownPublicPages = ["about","privacy","terms","employers","delete-data"];
  if (query && !knownPublicPages.includes(query) && !user && !authLoading) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <NotFoundPage />
      </Suspense>
    );
  }

  return (
    <ErrorBoundary>
      <PWAPrompt />
      {authLoading && <LoadingScreen />}

      {!authLoading && !user && (
        <AuthPage onAuthenticated={() => {}} />
      )}

      {!authLoading && user && needsOnboarding && (
        <OnboardingPage onComplete={(data) => {
          setProfile(prev => ({ ...(prev || {}), onboarded: true, role: data.role, goals: data.goals }));
        }} />
      )}

      {!authLoading && user && !needsOnboarding && <Shell />}
    </ErrorBoundary>
  );
}
